import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import Intercom, { boot, shutdown } from '@intercom/messenger-js-sdk';
import { IntercomSettings } from '@intercom/messenger-js-sdk/dist/types';

import { environment } from '@eai-tdv/environment';

import { IAuthUser } from '@eai-tdv/core/models';
import { DateTimeFormatService } from '@eai-tdv/core/services/date-time-format';
import { SupportChatService } from '@eai-tdv/core/services/support-chat';
import { TranslocoRootModule } from '@eai-tdv/core/transloco';

import { AuthUserStateService } from '../../services/auth-user-state.service';

@Component({
  imports: [TranslocoRootModule],
  selector: 'tdv-support-chat',
  templateUrl: './support-chat.component.html',
  styleUrls: ['./support-chat.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportChatComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  currUser!: IAuthUser | null;

  constructor(
    private readonly authUserStateS: AuthUserStateService,
    public readonly supportChatS: SupportChatService,
    public readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.initUserIdentification();
  }

  private initUserIdentification(): void {
    const delay = 2000; // just in case wait intercome async actions
    this.authUserStateS.authProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (user) {
          // changed user
          if (
            !this.currUser ||
            (this.currUser && user.uuid !== this.currUser.uuid)
          ) {
            this.fullReinitialization(); // !!!

            setTimeout(() => {
              boot(this.getLoggedInUserData(user));
            }, delay);
          }
        } else {
          // logout
          this.fullReinitialization(); // !!!

          setTimeout(() => {
            boot(this.getAnonymUserData());
          }, delay);
        }
        this.currUser = user;
      });
  }

  // !!! important both shutdown() Intercom() must be used for switch logged in/out users. boot, update etc don't works finw
  private fullReinitialization(): void {
    shutdown();
    Intercom(this.createCommonSettings());
  }

  private createCommonSettings(): IntercomSettings {
    const settings: IntercomSettings = {
      app_id: environment.intercom.appId,
      alignment: 'left',
    };
    return settings;
  }

  private getLoggedInUserData(user: IAuthUser): IntercomSettings {
    const createdAtSec =
      Math.ceil(
        DateTimeFormatService.transformApiFormatToNativeDateFormat(
          user.created_at,
        )!.getTime() / 1000,
      ) || 0;
    const settings: IntercomSettings = {
      ...this.createCommonSettings(),
      user_id: user.uuid,
      name: [user.first_name || '', user.last_name || ''].join(' '),
      email: user.email,
      created_at: createdAtSec,
      user_hash: user.intercom_hash,
    };
    return settings;
  }

  private getAnonymUserData(): IntercomSettings {
    const settings: IntercomSettings = this.createCommonSettings();
    return settings;
  }
}
